// Two-step sign-in (CEO 2026-10-21) — the second half of the Flow sign-in
// page, plus the pieces Settings reuses.
//
//   setup  → QR code + manual key → first 6-digit code → recovery codes
//   verify → 6-digit code (or a recovery code) → in
//
// While half signed in the browser holds only the challenge token from
// /auth/login (in memory, 10 minutes). It is sent as a Bearer token to the
// /auth/mfa/* endpoints and nowhere else; skipAuth + skipAuthRefresh keep the
// normal session machinery out of it. Large type, plain words (CEO).
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { ApiError, api } from "@/lib/api";
import type { SessionUser, TwoStepChallenge } from "@/auth/AuthProvider";

export interface SetupInfo {
  manual_key: string;
  otpauth_url: string;
  qr_data_url: string;
}

interface SessionBody {
  access_token: string;
  user: SessionUser;
}

/** Calls an /auth/mfa/* endpoint either half signed in (challenge) or as the
 *  signed-in user (Settings). */
export type MfaCaller = <T>(path: string, body?: unknown) => Promise<T>;

export function challengeCaller(challenge: TwoStepChallenge): MfaCaller {
  return <T,>(path: string, body?: unknown) =>
    api<T>(path, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
      headers: { Authorization: `Bearer ${challenge.token}` },
      skipAuth: true,
      skipAuthRefresh: true,
    });
}

export const sessionCaller: MfaCaller = <T,>(path: string, body?: unknown) =>
  api<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) });

/** Plain-words message for a failed code / setup call. */
export function twoStepErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const b = (err.body ?? {}) as { error?: string; retry_at?: string; reason?: string };
    if (err.status === 400 && b.error === "invalid_code") {
      return "That code didn't work. Check the app and try again — the code changes every 30 seconds.";
    }
    if (err.status === 400) return "Type the 6-digit code from the app.";
    if (err.status === 429) {
      return b.retry_at
        ? `Too many wrong codes. Wait until ${new Date(b.retry_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} and try again.`
        : "Too many wrong codes. Wait 15 minutes and try again.";
    }
    if (err.status === 401) return "That took too long. Please sign in again.";
    if (err.status === 503 && b.error === "mfa_misconfigured") return "Two-step sign-in is misconfigured — contact admin.";
    if (err.status === 503) return "Two-step sign-in is almost ready. Please try again in a little while.";
    if (err.status === 409) return "Two-step sign-in is already set up for you. Please sign in again.";
  }
  return "Something went wrong. Please try again in a moment.";
}

const inputCls =
  "h-[56px] w-full rounded-[4px] border border-neutral-300 bg-white px-4 text-[22px] tracking-[0.2em] text-black placeholder:tracking-normal placeholder:text-[17px] placeholder:text-neutral-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black";
const primaryBtn =
  "flex h-[54px] w-full items-center justify-center gap-3 rounded-[3px] bg-black text-[17px] font-semibold text-white transition-colors hover:bg-neutral-800 disabled:cursor-progress disabled:opacity-70";
const linkBtn = "text-[16px] text-neutral-700 underline-offset-4 hover:text-black hover:underline";

function ErrorBox({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <div role="alert" className="rounded-[4px] border border-rust-300 bg-rust-50 px-3 py-2 text-[16px] text-rust-600">
      {text}
    </div>
  );
}

function RememberBox({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 text-[17px] text-black">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-5 w-5 accent-black"
      />
      <span>
        Remember this device for 30 days
        <span className="block text-[15px] text-neutral-500">Only on a computer that is yours.</span>
      </span>
    </label>
  );
}

// ─── Setup: QR + manual key + first code ─────────────────────────────────────

export function AuthenticatorSetup({
  call,
  showRemember,
  onConfirmed,
}: {
  call: MfaCaller;
  showRemember: boolean;
  onConfirmed: (body: { recovery_codes: string[] } & Partial<SessionBody>) => void;
}) {
  const [setup, setSetup] = useState<SetupInfo | null>(null);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    call<SetupInfo>("/auth/mfa/setup/start")
      .then((s) => !cancelled && setSetup(s))
      .catch((e) => !cancelled && setError(twoStepErrorText(e)));
    return () => {
      cancelled = true;
    };
  }, [call]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body = await call<{ recovery_codes: string[] } & Partial<SessionBody>>("/auth/mfa/setup/confirm", {
        code: code.trim(),
        remember_device: remember,
      });
      onConfirmed(body);
    } catch (err) {
      setError(twoStepErrorText(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <ol className="space-y-6 text-[17px] leading-[1.45] text-black">
        <li>
          <p className="font-semibold">1. Open your authenticator app (Google Authenticator, Microsoft Authenticator or 1Password) and scan this code.</p>
          <div className="mt-4 flex justify-center rounded-[4px] border border-neutral-300 bg-white p-4">
            {setup ? (
              <img src={setup.qr_data_url} alt="QR code to scan with your authenticator app" width={220} height={220} />
            ) : (
              <div className="flex h-[220px] w-[220px] items-center justify-center text-[16px] text-neutral-500">
                {error ? "" : "Loading…"}
              </div>
            )}
          </div>
          {setup && (
            <p className="mt-3 text-[16px] text-neutral-600">
              Can't scan it? In the app choose "enter a setup key" and type:
              <span className="mt-2 block select-all break-all rounded-[4px] bg-white px-3 py-2 font-mono text-[18px] tracking-wider text-black">
                {setup.manual_key}
              </span>
            </p>
          )}
        </li>
        <li>
          <p className="font-semibold">2. Type the 6-digit code the app shows for "Other Flow".</p>
        </li>
      </ol>

      <form onSubmit={submit} className="space-y-5">
        <input
          aria-label="6-digit code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123456"
          className={inputCls}
          disabled={!setup}
        />
        {showRemember && <RememberBox checked={remember} onChange={setRemember} />}
        <ErrorBox text={error} />
        <button type="submit" disabled={busy || !setup} className={primaryBtn}>
          {busy ? "Checking…" : (<>Turn on two-step sign-in <span aria-hidden="true">→</span></>)}
        </button>
      </form>
    </div>
  );
}

// ─── Recovery codes (shown once) ─────────────────────────────────────────────

export function RecoveryCodesPanel({ codes, onDone, doneLabel }: { codes: string[]; onDone: () => void; doneLabel: string }) {
  const [copied, setCopied] = useState(false);
  const text = `Other Flow — recovery codes\nEach code signs you in once if you lose your phone.\n\n${codes.join("\n")}\n`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="space-y-6">
      <p className="text-[17px] leading-[1.45] text-black">
        If you ever lose your phone, each of these codes lets you sign in <strong>once</strong>. Save them somewhere safe —
        a password manager or a printed copy. They won't be shown again.
      </p>
      <ul className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-[4px] border border-neutral-300 bg-white px-5 py-4 font-mono text-[19px] tracking-wider text-black">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={copy} className="h-[48px] rounded-[3px] border border-black px-5 text-[16px] font-semibold text-black hover:bg-white">
          {copied ? "Copied" : "Copy codes"}
        </button>
        <a
          href={`data:text/plain;charset=utf-8,${encodeURIComponent(text)}`}
          download="other-flow-recovery-codes.txt"
          className="flex h-[48px] items-center rounded-[3px] border border-black px-5 text-[16px] font-semibold text-black hover:bg-white"
        >
          Download codes
        </a>
      </div>
      <button type="button" onClick={onDone} className={primaryBtn}>
        {doneLabel} <span aria-hidden="true">→</span>
      </button>
    </div>
  );
}

// ─── The sign-in page's second step ──────────────────────────────────────────

export function TwoStepSignIn({
  challenge,
  onSignedIn,
  onStartOver,
}: {
  challenge: TwoStepChallenge;
  onSignedIn: (accessToken: string, user: SessionUser) => void;
  onStartOver: () => void;
}) {
  const [call] = useState(() => challengeCaller(challenge));
  const [pendingSession, setPendingSession] = useState<SessionBody | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  // Signed in with a recovery code and few left → say so before going on.
  const [lowCodes, setLowCodes] = useState<{ left: number; session: SessionBody } | null>(null);

  // Verify step state
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (codes && pendingSession) {
    return (
      <Shell title="Save your recovery codes" onStartOver={null}>
        <RecoveryCodesPanel
          codes={codes}
          doneLabel="I've saved them — continue to Flow"
          onDone={() => onSignedIn(pendingSession.access_token, pendingSession.user)}
        />
      </Shell>
    );
  }

  if (lowCodes) {
    return (
      <Shell title="You're in — one thing first" onStartOver={null}>
        <div className="space-y-6">
          <p className="text-[17px] leading-[1.45] text-black">
            You signed in with a recovery code. You have <strong>{lowCodes.left}</strong> recovery code
            {lowCodes.left === 1 ? "" : "s"} left. Get new ones in Settings ▸ Two-step sign-in, and keep them somewhere safe.
          </p>
          <p className="text-[16px] text-neutral-600">For safety, every remembered device has been signed out.</p>
          <button type="button" className={primaryBtn} onClick={() => onSignedIn(lowCodes.session.access_token, lowCodes.session.user)}>
            Continue to Flow <span aria-hidden="true">→</span>
          </button>
        </div>
      </Shell>
    );
  }

  if (challenge.setup) {
    return (
      <Shell
        title="Set up two-step sign-in"
        intro="Flow now asks for a code from your phone each time you sign in. This takes about a minute, once."
        onStartOver={onStartOver}
      >
        <AuthenticatorSetup
          call={call}
          showRemember
          onConfirmed={(body) => {
            if (body.access_token && body.user) {
              setPendingSession({ access_token: body.access_token, user: body.user });
              setCodes(body.recovery_codes);
            }
          }}
        />
      </Shell>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body = await call<SessionBody & { method?: "totp" | "recovery"; recovery_codes_left?: number }>("/auth/mfa/verify", {
        code: code.trim(),
        remember_device: remember,
      });
      if (body.method === "recovery" && typeof body.recovery_codes_left === "number" && body.recovery_codes_left <= 3) {
        setLowCodes({ left: body.recovery_codes_left, session: body });
        return;
      }
      onSignedIn(body.access_token, body.user);
    } catch (err) {
      setError(twoStepErrorText(err));
      setCode("");
      setBusy(false);
    }
  };

  return (
    <Shell
      title="Enter your code"
      intro={'Open your authenticator app and type the 6-digit code for "Other Flow".'}
      onStartOver={onStartOver}
    >
      <form onSubmit={submit} className="space-y-5">
        <input
          aria-label="6-digit code or recovery code"
          autoFocus
          autoComplete="one-time-code"
          maxLength={20}
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123456"
          className={inputCls}
        />
        <p className="text-[16px] text-neutral-600">Lost your phone? Type one of your recovery codes instead.</p>
        <RememberBox checked={remember} onChange={setRemember} />
        <ErrorBox text={error} />
        <button type="submit" disabled={busy} className={primaryBtn}>
          {busy ? "Checking…" : (<>Sign in <span aria-hidden="true">→</span></>)}
        </button>
      </form>
    </Shell>
  );
}

function Shell({
  title,
  intro,
  onStartOver,
  children,
}: {
  title: string;
  intro?: string;
  onStartOver: (() => void) | null;
  children: ReactNode;
}) {
  return (
    <div>
      <h2 className="font-display text-[clamp(30px,2.6vw,38px)] font-bold tracking-[-0.03em] text-black">{title}</h2>
      {intro && <p className="mt-2 text-[17px] leading-[1.45] text-neutral-600">{intro}</p>}
      <div className="mt-8">{children}</div>
      {onStartOver && (
        <button type="button" onClick={onStartOver} className={`mt-6 ${linkBtn}`}>
          ← Use a different account
        </button>
      )}
    </div>
  );
}
